import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

interface DemoFormData {
  fullName: string;
  companyName: string;
  jobTitle: string;
  summary: string;
  niche: string;
}

const DemoForm: React.FC = () => {
  const [formData, setFormData] = useState<DemoFormData>({
    fullName: '',
    companyName: '',
    jobTitle: '',
    summary: '',
    niche: ''
  });
  const [emailPreview, setEmailPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setEmailPreview('');

    try {
      // First, save to Supabase
      const { error: supabaseError } = await supabase
        .from('website_demo')
        .insert([{
          full_name: formData.fullName,
          company_name: formData.companyName,
          job_title: formData.jobTitle,
          summary: formData.summary,
          niche: formData.niche
        }]);

      if (supabaseError) throw supabaseError;

      // Then call the webhook
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch('https://n8n-uq4a.onrender.com/webhook/ffa47db4-b82c-4a38-954a-6aa57854f029', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          'Full Name': formData.fullName,
          'Company Name': formData.companyName,
          'Job Title': formData.jobTitle,
          'Summary of Job Title at Conpany': formData.summary,
          'Niche': formData.niche
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const text = await response.text();
      try {
        const data = JSON.parse(text);
        if (Array.isArray(data) && data.length > 0 && data[0].message?.content?.icebreaker) {
          setEmailPreview(data[0].message.content.icebreaker);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (parseError) {
        throw new Error('Failed to parse response data');
      }

    } catch (err) {
      console.error('Error:', err);
      let errorMessage = 'An error occurred while generating your email.';
      
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else {
          errorMessage = `Error: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full bg-white p-6 rounded-lg shadow-lg mt-24">
      <div className="mb-6 text-center">
        <p className="text-gray-600">Fill in the form below with sample data and click "Generate Personalized Email" to see a personalized email created just for you!</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            type="text"
            id="fullName"
            required
            placeholder="e.g., John Smith"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0aa19a] focus:ring focus:ring-[#0aa19a] focus:ring-opacity-50"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            id="companyName"
            required
            placeholder="e.g., Tech Solutions Inc"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0aa19a] focus:ring focus:ring-[#0aa19a] focus:ring-opacity-50"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
            Job Title
          </label>
          <input
            type="text"
            id="jobTitle"
            required
            placeholder="e.g., Marketing Manager"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0aa19a] focus:ring focus:ring-[#0aa19a] focus:ring-opacity-50"
            value={formData.jobTitle}
            onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-1">
            Summary of Job Title at Company
          </label>
          <textarea
            id="summary"
            required
            placeholder="e.g., Leading the marketing team and developing strategic campaigns"
            rows={3}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0aa19a] focus:ring focus:ring-[#0aa19a] focus:ring-opacity-50"
            value={formData.summary}
            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="niche" className="block text-sm font-medium text-gray-700 mb-1">
            Niche
          </label>
          <input
            type="text"
            id="niche"
            required
            placeholder="e.g., B2B Software"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0aa19a] focus:ring focus:ring-[#0aa19a] focus:ring-opacity-50"
            value={formData.niche}
            onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
          />
        </div>

        {error && (
          <div className="text-red-600 text-sm text-center p-3 bg-red-50 rounded-md border border-red-200">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-[#0aa19a] hover:bg-[#224c5c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0aa19a] transition-colors duration-200 disabled:opacity-50"
        >
          {isLoading ? 'Generating Email...' : 'Generate Personalized Email'}
        </button>
      </form>

      {emailPreview && (
        <div className="mt-6">
          <div className="bg-gray-50 rounded-md p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Your AI-Generated Email:</h3>
            <p className="text-sm text-gray-600 mb-4">Here's a preview of how our AI would reach out to someone with your profile:</p>
            <div className="whitespace-pre-wrap text-gray-700 bg-white p-4 rounded border border-gray-200">{emailPreview}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoForm;